import './wm-support-conversation-entry-editor';
import '../conversation/components/wm-support-ticket-header';
import {
  BranchQuoteSupport,
  BranchQuoteSupportItemType,
  BranchQuoteSupportStatus,
  ViewConversationEntry
} from '../../api/dealer-api-interface-franchisee';

import { currentUserClaims } from '../../../webmodule-common/other/currentuser-claims';
import { getCurrentUser } from '../../../webmodule-common/other/api/current-user';
import { customElement, property, query, state } from 'lit/decorators.js';
import { emptyGuid, newGuid } from '../../../webmodule-common/other/api/guid';
import { getApiFactory } from '../../api/api-injector';
import { getSupportStatusBadge } from './conversation-helper';
import { SupportTicketService } from './support-ticket-service';
// Legacy support-events removed (snapshot subscription model)
// Legacy guard removed.
import { deriveSupportCapabilities, renderSupportDebugBanner } from './support-capabilities';
import { html, TemplateResult } from 'lit';
import { LitElementBase } from '../../../webmodule-common/other/litelement-base';
import { LangBranchQuoteSupportEditor } from '../../../localization/scripts/dealer-franchisee/conversation/wm-branchquote-support-editor-lang';
import { FranchiseeQuoteContainerManager } from '../quotes/data/franchisee-quote-manager';

@customElement('wmview-branchquote-conversation-editor')
export class WebModuleBranchQuoteConversationEditor extends LitElementBase {
  // Option C refactor: parent supplies a direct callback instead of listening for legacy @wm-event-close
  @property({ attribute: false }) onClose?: () => void;
  isEditingDraft: any;
  @state() quoteTitle?: TemplateResult;
  // Conversation state now sourced directly from SupportTicketService snapshots to avoid duplication.
  // We rely on subscription calling requestUpdate() instead of mutating local arrays.
  @property({ attribute: false })
  branchQuoteSupport?: BranchQuoteSupport;
  @property() concurrency = '';
  @query('#conversationSupportEditor')
  conversation?: any;
  @property() supplierName = '';
  protected claims = currentUserClaims();
  protected conversationEntryId: string = newGuid();
  protected quoteApi = getApiFactory().quote();

  protected franchiseapi = getApiFactory().franchisee();
  // conversationActions removed (legacy actions template obsolete)
  @property({ type: Object, attribute: false }) quoteManager: FranchiseeQuoteContainerManager;

  constructor() {
    super();
  }

  get forceReadonly() {
    return this.isReadonly || this.ticketClosed;
  }

  get branchQuoteSupportId(): string {
    return this.branchQuoteSupport?.id ?? emptyGuid;
  }

  protected get ticketClosed() {
    // A ticket is considered closed (read-only) only if its status is explicitly one of the terminal states.
    // This should NOT trigger any automatic tab closure – parent tab remains open until user manually closes it.
    if (!this.branchQuoteSupport) return true; // absence implies not yet loaded / treat as non-editable
    const status = this.branchQuoteSupport.status;
    return status === BranchQuoteSupportStatus.Cancelled || status === BranchQuoteSupportStatus.Resolved;
  }

  protected async firstUpdated() {
    if (this.conversation) this.dispatchCustom('!ui-changed', { ui: this });
  }

  protected log(s) {
    console.log(s);
  }

  // Legacy inline edit handler removed – handled via request-edit-entry event from conversation list

  private onRequestEditEntry = (e: CustomEvent<{ entry: ViewConversationEntry }>) => {
    e.stopPropagation();
    if (!this.branchQuoteSupport) return;
    const service: SupportTicketService | undefined = this.quoteManager?.supportTicketService;
    if (!service) return;
    const session = service.getSession(this.branchQuoteSupport.id);
    service.sessionSetEditingEntry(session, e.detail.entry.id);
  };

  private _unsubscribe?: () => void;
  // Computed snapshot-backed conversation entries
  private get entries(): ViewConversationEntry[] {
    try {
      const id = this.branchQuoteSupport?.id;
      if (!id) return [];
      const service: SupportTicketService | undefined = this.quoteManager?.supportTicketService;
      if (!service) return [];
      const state = service.getState();
      const snap = state.tickets[id];
      return snap?.entries ?? [];
    } catch {
      return [];
    }
  }
  private get editingEntryId(): string | null {
    try {
      const id = this.branchQuoteSupport?.id;
      if (!id) return null;
      const service: SupportTicketService | undefined = this.quoteManager?.supportTicketService;
      if (!service) return null;
      return service.getEditingEntryId(id) ?? null;
    } catch {
      return null;
    }
  }
  connectedCallback(): void {
    super.connectedCallback();
    const service: SupportTicketService | undefined = this.quoteManager?.supportTicketService;
    if (!service) return;
    this._unsubscribe = service.subscribe((state, delta) => {
      if (!this.branchQuoteSupport) return;
      const id = this.branchQuoteSupport.id;

      if (delta.changedTicketIds.length && !delta.changedTicketIds.includes(id)) return;
      const snap = state.tickets[id];
      if (!snap) return;
      // Auto-select existing user draft if none selected
      if (!this.editingEntryId) {
        const currentUserId = getCurrentUser()?.id;
        if (currentUserId) {
          const existingDraft = this.entries.find(e => e.draft && e.systemUserId === currentUserId);
          if (existingDraft) {
            const session = service.getSession(id);
            service.sessionSetEditingEntry(session, existingDraft.id);
          }
        }
      }

      // Trigger re-render to reflect latest snapshot-backed getters
      this.requestUpdate();
    });
  }
  disconnectedCallback(): void {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = undefined;
    }
    super.disconnectedCallback();
  }

  async updated(): Promise<void> {}

  private get editingEntry(): ViewConversationEntry | undefined {
    const id = this.editingEntryId;
    return id ? this.entries.find(e => e.id === id) : undefined;
  }

  // Raw events now bubble to parent view; cancel editor maps to clearing editing entry directly.
  private onCancelEditor = () => {
    if (!this.branchQuoteSupport) return;
    const service: SupportTicketService | undefined = this.quoteManager?.supportTicketService;
    if (!service) return;
    const session = service.getSession(this.branchQuoteSupport.id);
    service.sessionSetEditingEntry(session, null);
  };

  private renderExternalEditor(): TemplateResult | '' {
    if (!this.branchQuoteSupport) return '';
    const ticketId = this.branchQuoteSupport.id;
    const entry = this.editingEntry;
    const mode = entry ? 'edit' : 'create';
    // Strengthen guard: never allow add/edit UI if ticket is in a terminal state (Resolved/Cancelled)
    const terminal = this.ticketClosed;
    const canAdd = !terminal && this.evaluateCapabilities().actions.addNote;
    if (terminal) return '';
    if (!canAdd && !entry) return '';
    const header = entry
      ? entry.draft
        ? LangBranchQuoteSupportEditor.editDraftComments()
        : LangBranchQuoteSupportEditor.editComments()
      : LangBranchQuoteSupportEditor.addComments();
    return html`<div class="conversation-note">
      <h2 class="main-header-style">${header}</h2>
      <wm-support-conversation-entry-editor
        .ticketId=${ticketId}
        .mode=${mode as any}
        .entry=${entry}
        .quoteManager=${this.quoteManager}
        @cancel=${this.onCancelEditor}
      ></wm-support-conversation-entry-editor>
    </div>`;
  }

  protected render(): unknown {
    const evalResult = this.evaluateCapabilities();
    const readonly = evalResult.readonly;
    // feature flag removed
    const conversation =
      this.branchQuoteSupport && this.quoteManager
        ? html`
            <div class="border-top border-secondary mt-2 ">
              <wm-conversation-view
                id="conversationSupportEditor"
                .supplierName=${this.supplierName}
                ?sortAscending=${true}
                .entries=${this.entries}
                .readonly=${readonly || !evalResult.actions.addNote || this.ticketClosed}
                emptyNote=""
                @request-edit-entry=${this.onRequestEditEntry}
              ></wm-conversation-view>
            </div>
          `
        : html``;

    const header = html`<wm-support-ticket-header
      .ticket=${this.branchQuoteSupport}
      .quoteManager=${this.quoteManager as any}
      .readonly=${readonly}
    ></wm-support-ticket-header>`;
    const typeHeader =
      this.branchQuoteSupport?.type == BranchQuoteSupportItemType.EngineeredToOrder
        ? LangBranchQuoteSupportEditor.specialItem()
        : LangBranchQuoteSupportEditor.supportRequest();

    const mainHeader = html` <div
      class="main-header d-flex justify-content-between  border-bottom border-secondary mb-2"
    >
      <h2>${typeHeader} ${getSupportStatusBadge(this.branchQuoteSupport?.status)}</h2>
    </div>`;

    const debugBanner = renderSupportDebugBanner(evalResult, 'Editor');
    return html` <div class="support-ticket-editor-main form-two-col ">
      ${debugBanner}
      <div class="row">
        <div class="eto-left form-column">
          ${mainHeader}
          <div>
            <div class="support-ticket-editor-container conversation">
              <div class="row">${header}</div>
            </div>
          </div>
          ${this.renderExternalEditor()}
        </div>
        <div class="eto-right form-column">
          <div class="conversation-header-block">
            <h2 class="main-header-style border-0">${LangBranchQuoteSupportEditor.comments()}</h2>
            <!-- attachmentsOnlySwitch removed with legacy actions; TODO: re-add via new actions component if needed -->
          </div>
          ${conversation}
        </div>
      </div>
    </div>`;
  }

  // Inline action rendering removed; parent view is responsible for rendering action buttons.

  private evaluateCapabilities() {
    return deriveSupportCapabilities({
      quoteManager: this.quoteManager,
      ticketStatus: this.branchQuoteSupport?.status,
      forceReadonly: this.isReadonly || this.ticketClosed,
      transientBusy: false,
      phase: 'editor'
    });
  }
}
